import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from '@/components/ui/SignaturePad';
import StatusBadge from '@/components/ui/StatusBadge';
import { ApiError } from '@/lib/api';
import { onboardingApi, type Approval, type ListingsPayload } from '@/lib/onboarding';
import { Field, Section } from './fields';
import { areaCls, btnPrimary, fmtAed, inputCls } from './styles';

interface Props {
  data: ListingsPayload;
  onReload: () => void;
  onToast: (message: string, tone?: 'success' | 'error') => void;
  canEdit: boolean;
}

/** Шаг 4: поставщик снимает галочки с позиций, которые размещать нельзя, и подтверждает остальное подписью на планшете. */
export default function ApprovalStep({ data, onReload, onToast, canEdit }: Props) {
  const { t, i18n } = useTranslation();
  const rows = data.rows.filter((r) => r.productStatus !== 'Discontinued' && r.listingStatus !== 'archived');
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set(rows.filter((r) => r.excludedByPartner).map((r) => r.listing.sku)));
  const [method, setMethod] = useState('tablet_signature');
  const [name, setName] = useState(data.partner.contact ?? '');
  const [contact, setContact] = useState(data.partner.email ?? '');
  const [notes, setNotes] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<Approval[]>([]);

  useEffect(() => {
    onboardingApi.approvals(data.partner.id).then(setHistory).catch(() => setHistory([]));
  }, [data.partner.id, data.summary.approval?.id]);

  const toggle = (sku: string) => setExcluded((s) => { const n = new Set(s); if (n.has(sku)) n.delete(sku); else n.add(sku); return n; });
  const approvedCount = rows.length - excluded.size;
  const canSubmit = canEdit && rows.length > 0 && name.trim() && (method !== 'tablet_signature' || signature);
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language === 'ru' ? 'ru-RU' : 'en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const submit = async () => {
    setBusy(true);
    try {
      const a = await onboardingApi.approve(data.partner.id, { method, approvedByName: name.trim(), approvedByContact: contact || null, signature: method === 'tablet_signature' ? signature : null, excludedSkus: [...excluded], notes: notes || null });
      onToast(t('onb.apprDone', { count: a.approvedSkusCount }));
      setSignature(null);
      onReload();
    } catch (e) {
      onToast(t('onb.error', { code: (e as ApiError).code }), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
      <div className="xl:col-span-2">
        <Section title={t('onb.apprTitle')} action={<span className="text-[11.5px] text-foreground-500">{t('onb.apprSelected', { count: approvedCount, excluded: excluded.size })}</span>}>
          <p className="mb-4 text-[12.5px] leading-5 text-foreground-600">{t('onb.apprDesc')}</p>
          {rows.length === 0 ? (
            <p className="text-[12.5px] text-foreground-500">{t('onb.apprNeedProducts')}</p>
          ) : (
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {rows.map((r) => {
                const ex = excluded.has(r.listing.sku);
                return (
                  <li key={r.listing.sku} className={`flex items-center gap-3 rounded-md border p-2 ${ex ? 'border-rose-200 bg-rose-50/40' : 'border-background-200 bg-background-50'}`}>
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded bg-white">{r.listing.images[0] ? <img src={r.listing.images[0].url} alt="" className="max-h-[90%] max-w-[90%] object-contain" /> : <i className="ri-image-line text-foreground-300" />}</div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12.5px] font-medium text-foreground-900">{r.listing.name.en}</p>
                      <p className="tnum text-[11px] text-foreground-500">{r.listing.sku} · AED {fmtAed(r.prices.b2c)} · {r.listing.availability.qty} pcs</p>
                    </div>
                    <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] text-foreground-600">
                      <input type="checkbox" checked={ex} disabled={!canEdit} onChange={() => toggle(r.listing.sku)} className="h-4 w-4 accent-rose-600" />
                      {t('onb.apprExclude')}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </Section>
      </div>
      <div className="flex flex-col gap-4">
        <Section title={t('onb.apprSign')}>
          <div className="flex flex-col gap-3">
            <Field label={t('onb.apprMethod')}>
              <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)} disabled={!canEdit}>
                {['tablet_signature', 'sms_code', 'portal', 'scan'].map((m) => <option key={m} value={m}>{t(`onb.methods.${m}`)}</option>)}
              </select>
            </Field>
            <Field label={t('onb.apprName')}><input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} disabled={!canEdit} /></Field>
            <Field label={t('onb.apprContact')}><input className={inputCls} value={contact} onChange={(e) => setContact(e.target.value)} disabled={!canEdit} /></Field>
            {method === 'tablet_signature' && <SignaturePad onChange={setSignature} clearLabel={t('onb.apprClear')} hint={t('onb.apprSign')} />}
            <Field label={t('onb.apprNotes')}><textarea className={areaCls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit} /></Field>
            <button type="button" onClick={submit} disabled={!canSubmit || busy} className={btnPrimary}>
              <i className="ri-quill-pen-line text-[14px]" /> {busy ? t('onb.apprSubmitting') : t('onb.apprSubmit')}
            </button>
          </div>
        </Section>
        <Section title={t('onb.apprHistory')}>
          {history.length === 0 ? (
            <p className="text-[12px] text-foreground-500">{t('onb.apprNone')}</p>
          ) : (
            <ul className="space-y-2 text-[12px]">
              {history.map((a) => (
                <li key={a.id} className="flex flex-col gap-1 border-b border-background-200 pb-2 last:border-0">
                  <span className="text-foreground-900">{t('onb.apprRow', { name: a.approvedByName, method: t(`onb.methods.${a.method}`), count: a.approvedSkusCount, excluded: a.excludedSkus.length })}</span>
                  <span className="flex items-center gap-2 text-[11px] text-foreground-500">
                    {fmt(a.approvedAt)} {a.hasSignature && <StatusBadge tone="primary" dot={false}>{t('onb.methods.tablet_signature')}</StatusBadge>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}
